import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import {
	EmailDeliverabilityFeatureAvailability,
	RISKY_EMAIL_POLICIES,
	RiskyEmailPolicy,
} from "@signalist/shared-types";

import { Radio } from "@/components/Radio/Radio";
import { Toggle } from "@/components/Toggle/Toggle";
import { classNames } from "@/utils/classNames";

import { EmailDeliverabilityValues } from "../utils/contactSettingsDeliverability";
import { ScopeBadge, SettingScope } from "./ScopeBadge";

interface EmailDeliverabilityCardsProps {
	availability: EmailDeliverabilityFeatureAvailability;
	values: EmailDeliverabilityValues;
	onChange: (patch: Partial<EmailDeliverabilityValues>) => void;
}

const CARD_CLASS = "bg-brand-white rounded-lg border border-border-light p-6 mb-4";

/**
 * The workspace email-deliverability cards on Contacts & Enrichment (PREC-4261). Each card renders
 * only once the slice that acts on it has shipped — its constant in `@signalist/shared-types` is
 * `true` — so no card offers a setting that changes nothing: verification (5/14), the risky-email
 * policy and the 4% pause (10/14), bounce repair and its company-change routing (8/14).
 */
export const EmailDeliverabilityCards: FunctionComponent<EmailDeliverabilityCardsProps> = ({
	availability,
	values,
	onChange,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`contacts.settings.${key}`);

	return (
		<>
			{availability.verification && (
				<div className={CARD_CLASS} data-testid="email-verification-settings-card">
					<div className="flex items-start justify-between">
						<div className="flex-grow mr-4">
							<div className="flex items-center gap-2 mb-1">
								<h4 className="text-sm font-semibold text-text">{ts("emailVerification.title")}</h4>
								<ScopeBadge label={ts("overridablePerList")} scope={SettingScope.PER_LIST} />
							</div>
							<p className="text-xs text-text-meta">{ts("emailVerification.description")}</p>
						</div>
						<Toggle
							isChecked={values.emailVerificationEnabled}
							testId="email-verification-toggle"
							onChange={() =>
								onChange({ emailVerificationEnabled: !values.emailVerificationEnabled })
							}
						/>
					</div>
				</div>
			)}

			{availability.riskyEmailPolicy && (
				<div className={CARD_CLASS} data-testid="risky-email-policy-settings-card">
					<div className="flex items-center gap-2 mb-1">
						<h4 className="text-sm font-semibold text-text">{ts("riskyEmails.title")}</h4>
						<ScopeBadge label={ts("overridablePerList")} scope={SettingScope.PER_LIST} />
					</div>
					<p className="text-xs text-text-meta mb-4">{ts("riskyEmails.description")}</p>

					<div className="flex flex-col gap-3">
						{RISKY_EMAIL_POLICIES.map((policy) => (
							<RiskyPolicyRow
								key={policy}
								isSelected={values.riskyEmailPolicy === policy}
								policy={policy}
								onSelect={() => onChange({ riskyEmailPolicy: policy })}
							>
								{policy === RiskyEmailPolicy.AUTO && values.riskyEmailPolicy === policy && (
									<div className="mt-3 pt-3 border-t border-border-light flex items-start gap-3">
										<Toggle
											isChecked={values.riskyEmailPauseAbove4Percent}
											testId="risky-email-pause-toggle"
											onChange={() =>
												onChange({
													riskyEmailPauseAbove4Percent: !values.riskyEmailPauseAbove4Percent,
												})
											}
										/>
										<div className="flex flex-col">
											<span className="text-sm text-text">
												{ts("riskyEmails.pauseAbove4Percent")}
											</span>
											<p className="text-xs text-text-meta">
												{ts("riskyEmails.pauseAbove4PercentDescription")}
											</p>
										</div>
									</div>
								)}
							</RiskyPolicyRow>
						))}
					</div>
				</div>
			)}

			{availability.bounceRepair && (
				<div className={CARD_CLASS} data-testid="bounce-repair-settings-card">
					<div className="flex items-start justify-between">
						<div className="flex-grow mr-4">
							<div className="flex items-center gap-2 mb-1">
								<h4 className="text-sm font-semibold text-text">{ts("bounceRepair.title")}</h4>
								<ScopeBadge label={ts("workspaceWide")} />
							</div>
							<p className="text-xs text-text-meta">{ts("bounceRepair.description")}</p>
						</div>
						<Toggle
							isChecked={values.bounceRepairEnabled}
							testId="bounce-repair-toggle"
							onChange={() => onChange({ bounceRepairEnabled: !values.bounceRepairEnabled })}
						/>
					</div>

					<div className="mt-4 flex items-start gap-3">
						<Toggle
							isChecked={values.bounceRepairReviewOnCompanyChange}
							testId="bounce-repair-review-toggle"
							onChange={() =>
								onChange({
									bounceRepairReviewOnCompanyChange: !values.bounceRepairReviewOnCompanyChange,
								})
							}
						/>
						<div className="flex flex-col">
							<span className="text-sm text-text">{ts("bounceRepair.reviewOnCompanyChange")}</span>
							<p className="text-xs text-text-meta">
								{ts("bounceRepair.reviewOnCompanyChangeDescription")}
							</p>
						</div>
					</div>
				</div>
			)}
		</>
	);
};

interface RiskyPolicyRowProps {
	policy: RiskyEmailPolicy;
	isSelected: boolean;
	onSelect: () => void;
	children?: React.ReactNode;
}

/** One radio row of the risky-email card; the AUTO row carries the nested 4% pause toggle. */
const RiskyPolicyRow: FunctionComponent<RiskyPolicyRowProps> = ({
	policy,
	isSelected,
	onSelect,
	children,
}) => {
	const { t } = useTranslation();

	return (
		<div
			className={classNames(
				"rounded-lg border p-4",
				isSelected ? "border-blue-action" : "border-border-light"
			)}
			data-testid={`risky-email-policy-${policy}`}
		>
			<Radio isChecked={isSelected} name="riskyEmailPolicy" value={policy} onChange={onSelect}>
				<span className="flex flex-col">
					<span className="flex items-center gap-2 text-sm font-medium text-text">
						{t(`contacts.settings.riskyEmails.policy.${policy}`)}
						{policy === RiskyEmailPolicy.AUTO && (
							<span className="rounded bg-status-success-light px-1.5 text-xs text-status-success">
								{t("contacts.settings.riskyEmails.recommended")}
							</span>
						)}
					</span>
					<span className="text-xs text-text-meta">
						{t(`contacts.settings.riskyEmails.policyDescription.${policy}`)}
					</span>
				</span>
			</Radio>
			{children}
		</div>
	);
};
